import React, { useEffect, useState, useRef, Fragment } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress'
import { Alert, AlertTitle, Grid, List, ListItem, ListItemButton, ListItemText, Stack } from "@mui/material";
import { CSVLink } from "react-csv";
import { FacebookShareButton } from 'react-share';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { DataGrid, GridToolbarExport } from '@mui/x-data-grid';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import Snackbar from '@mui/material/Snackbar';
import RefreshIcon from '@mui/icons-material/Refresh';
import _debounce from 'lodash.debounce';
import {
    Link,
    useRouteMatch,
    useLocation
} from "react-router-dom";

// local utils
import { ServerErrorMessage } from './PageNotFound';
import { loadPublicList } from '../actions/publicList';
import LineChart from '../charts/LeaderBoardChart';
import { useWindowDimensions } from './Utils';


// snackbar rendering issue not fixed
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const LeaderBoard = ({ lastFetched, listLoading, publicWatchlist, navbarSize, loadPublicList }) => {
    const dispatchLoad = loadPublicList; // for clarity

    // Initial data fetch logic
    useEffect(() => {
        const now = Date.now();
        const isStale = !lastFetched || (now - lastFetched > STALE_THRESHOLD_MS);

        // Fetch if data is stale or has never been fetched.
        // An empty publicWatchlist after a successful fetch (indicated by lastFetched) is valid.
        if (isStale) {
            dispatchLoad();
        }
    // Only re-run if dispatchLoad (stale) or lastFetched (timestamp of last fetch) changes.
    }, [dispatchLoad, lastFetched]);


    let queryName = useQuery().get('name');
    const [loading, setLoading] = useState(true); // Initialize local loading to true
    const [serverError, setServerError] = useState(false);
    const [listCount, setListCount] = useState(publicWatchlist.length);
    const [activeButton, setActiveButton] = useState(0);
    const [tweetMessage, setTweetMessage] = useState('');
    const [csvData, setCsvData] = useState('');
    const [rows, setRows] = useState([]);
    
    // active list info
    const [openList, setOpenList] = useState(null);
    const [openlistName, setOpenlistName] = useState('');
    const [openlistSymbols, setOpenlistSymbols] = useState([]);
    const [openlistDates, setOpenlistDates] = useState([]);

    const handleInitSet = () => {
        if (publicWatchlist && publicWatchlist.length > 0) {
            let al = publicWatchlist[0]
            setOpenList(al);
            setOpenlistName(al.listname);
            setOpenlistSymbols(al.items.map(v => v.symbol));
            // Use per-item created date if available, else fallback to list created
            setOpenlistDates(al.items.map(item => (item.created || al.created || '').split('T')[0]));
        } else {
            // Reset if publicWatchlist is empty or null
            setOpenList(null);
            setOpenlistName('');
            setOpenlistSymbols([]);
            setOpenlistDates([]);
        }
    };
    // Removed standalone useEffect for handleInitSet as it's incorporated below

    // Effect for managing component state based on loading status and data
    useEffect(() => {
        setLoading(listLoading); // Keep local loading in sync with Redux loading state

        if (!listLoading) { // When loading is finished
            if (publicWatchlist) { // publicWatchlist is expected to be an array (empty or populated)
                setServerError(false); // Assume success if loading is done and we have an array
                handleInitSet();
                setListCount(publicWatchlist.length);
                handleQueryName(); // Process query parameter for list name
            } else {
                // This case (publicWatchlist is null/undefined after load) might indicate an error
                setServerError(true); // Assume error if data is not an array after load
                handleInitSet(); // Reset openList etc.
                setListCount(0);
            }
        }
    }, [listLoading, publicWatchlist, queryName]); // queryName is a dependency for handleQueryName

    // Effect to save public watchlist data to local storage upon successful fetch
    useEffect(() => {
        if (!listLoading && publicWatchlist && lastFetched) {
            try {
                localStorage.setItem('public-list', JSON.stringify({ data: publicWatchlist }));
                // localStorage.setItem('publicWatchlistLastFetched', lastFetched.toString());
            } catch (error) {
                console.error('LeaderBoard: Error saving public watchlist data to local storage:', error);
            }
        }
    }, [publicWatchlist, lastFetched, listLoading]);

    const handleTableRows = (al) => {
        if (al !== null && al.items) {
            setRows(
                al.items.map( (item, index) => {
                    return {
                        id: index,
                        symbol: item.symbol,
                        init_price: Number(item.init_price).toFixed(3),
                        curr_price: Number(item.current_price).toFixed(3),
                        change: item.price_change,
                        percent_change: item.percent_change,
                        init_date: (item.created || al.created || '').split('T')[0],
                    }
                })
            );    
        }
    };
    // useEffect( () => { handleTableRows()}, []);

    useEffect( () => { 
        handleTableRows(openList);
        handleCsvData(openList);
        handleTweetMessage(openList);
    }, [openList])


    const handleRefresh = () => {
        window.location.reload();
    }


    const handleQueryName = () => {
        if (queryName !== null && publicWatchlist && publicWatchlist.length > 0) {
            let idx = publicWatchlist.findIndex( v => v.listname === queryName);
            if (publicWatchlist[idx] && publicWatchlist[idx].listname === queryName) {
                let al = publicWatchlist[idx]
                setOpenList(al);
                setOpenlistName(al.listname);
                setOpenlistSymbols(al.items.map(item => item.symbol));
                setOpenlistDates(al.items.map(item => (item.created || al.created || '').split('T')[0]));
                setActiveButton(idx);
            } else {
                // If queryName is not found, handleInitSet (called above) will default to the first list or empty state.
                // The NoMatch component will be rendered if queryName is present but not valid.
            }
        }
    };

    // console.log(openList ? openList.Name : 'no list name');
    // console.log(csvData);
    // console.log(queryName);

    const cardColor = '#f5f5f5';
    
    const columns = [
        { field: 'symbol', headerName: 'Symbol', width: 100 },
        { field: 'init_price', headerName: 'Initial Price', width: 100 },
        { field: 'curr_price', headerName: 'Current Price', width: 100 },
        { field: 'change', headerName: 'Change $', width: 100 },
        { field: 'percent_change', headerName: '% Change', width: 100 },
        { field: 'init_date', headerName: 'Initial Date', width: 150 },
    ];


    const handleListItemClick = (name, idx) => {
        // console.log(name, idx)
        let al = publicWatchlist[idx]
        setOpenList(al);
        setOpenlistName(al.listname);
        setOpenlistSymbols(al.items.map(item => item.symbol));
        setOpenlistDates(al.items.map(item => (item.created || al.created || '').split('T')[0]));
        handleTweetMessage(al);
        handleCsvData(al);
        setActiveButton(idx);
    };

    
    const handleKeyPress = (e) => {
        
        if (e.keyCode === 40) {
            if (activeButton+1 < listCount) {
                setActiveButton(prev => prev+1)
            } else {
                setActiveButton(0)
            }
        } else if (e.keyCode === 38) {
            if (activeButton > 0) {
                setActiveButton(prev => prev-1)
            } else {
                setActiveButton(listCount-1)
            }
        };
        
    };    
    
    // copy chart to clipboard
    // fix rendering issue with Snack actions
    const [openSnack, setOpenSnack] = useState(false);
    const handleSnackClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnack(false);
    };

    const handleChartCopy = () => {
        // console.log('copy chart');
        if (chartRef.current !== null) {
            var canvas = chartRef.current.ctx.canvas;

            try {
                canvas.toBlob(blob => navigator.clipboard.write([new window.ClipboardItem({'image/png': blob})]));
                // setOpenSnack(true);
            } catch (error) {
                connect.error(error)
            }    
        }
    };

    const handleCsvData = (al) => {
        if (al && al.items) {
            setCsvData(al.items.map(
                (t) => ({Symbol: t.symbol, InitialPrice: t.init_price, CurrentPrice: t.current_price, Change: t.price_change, InitialDate: (t.created || al.created || '').split('T')[0]})
            ))
        } else {
            setCsvData('')
        }
    };

    const handleTweetMessage = (al) => {
        if (al && al.items) {
            const listItems = '$' + al.items.map(t => t.symbol).join(', $');
            // Use the earliest created date among items, fallback to al.created
            let publishedDate = '';
            if (al.items.length > 0) {
                publishedDate = al.items.map(t => t.created).filter(Boolean).sort()[0] || al.created || '';
            } else {
                publishedDate = al.created || '';
            }
            publishedDate = (publishedDate || '').split('T')[0];
            // Use total_return from the list instead of summing item returns
            const totalReturn = al.total_return || 0;
            const tweetMessage = `Published: ${publishedDate}   Return: $${totalReturn}      ${listItems}`;
            setTweetMessage(tweetMessage);
        } else {
            setTweetMessage('');
        }    
    }



    // setting table dimensions *** potential to improve ***
    const chartRef = useRef(null);
    const mainBoxHeight = useWindowDimensions().height - navbarSize - 15;


    return (
    <div> { loading ?
        <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
        }}>
            <CircularProgress size={60} />
        </div> : serverError ?
        <div>
            <ServerErrorMessage />
        </div> : (!publicWatchlist || publicWatchlist.length === 0) ?
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: mainBoxHeight, flexDirection: 'column' }}>
            <Alert severity="info" sx={{ width: 'fit-content', '& .MuiAlert-icon': { fontSize: 40 }, '& .MuiAlert-message': { fontSize: '1.2rem', alignSelf: 'center' } }}>
                <AlertTitle sx={{ fontSize: '1.5rem' }}>No Public Lists Available</AlertTitle>
                There are currently no public watchlists to display.
                <br />
                Check back later or be the first to create one!
            </Alert>
            <IconButton onClick={handleRefresh} sx={{marginTop: '20px'}}><RefreshIcon fontSize="large" /> <span style={{marginLeft: '8px'}}>Refresh</span></IconButton>
        </div> :
        <Grid container style={{height: mainBoxHeight, marginTop: '10px'}}>
            <Grid  item xs={2.5} style={{backgroundColor: cardColor, overflow: 'auto', height: mainBoxHeight}}>
                <LeftPanel
                    publicList={publicWatchlist}
                    activeButton={activeButton}
                    cardColor={cardColor}
                    handleListItemClick={handleListItemClick}
                    panelHeight={mainBoxHeight}
                    handleKeyPress={handleKeyPress}
                ></LeftPanel>
            </Grid>

            <Grid item xs={9}  style={{paddingLeft: '1rem', paddingRight: '1rem', height: mainBoxHeight}}>
                { publicWatchlist.map( v => v.listname).includes(queryName) || queryName === null ?
                <MainPanel
                    openlistName = {openlistName}
                    openlistSymbols={openlistSymbols}
                    openlistDates={openlistDates}
                    rows={rows}
                    columns={columns}
                    cardColor={cardColor}
                    mainBoxHeight={mainBoxHeight}
                    chartRef = {chartRef}
                ></MainPanel>:
                <NoMatch name={queryName}></NoMatch>
                }
            </Grid>

            <Grid item xs={.5}  style={{background: cardColor, height: mainBoxHeight}}>
                <RightPanel
                    tweetMessage={tweetMessage}
                    csvData={csvData}
                    activelistName={openlistName}
                    handleChartCopy={handleChartCopy}
                    handleRefresh={handleRefresh}
                ></RightPanel>
            </Grid>
        </Grid>
        }

        <div>
            <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            open={openSnack}
            autoHideDuration={3000}
            onClose={handleSnackClose}
            message="Image copied to clipboard"
            />
        </div>
    </div>
    )
}

const mapStateToProps = (state) => {
    // console.log("lastFetched:", state.publicList.lastFetched);
    return {
        publicWatchlist: state.publicList.publicList,
        lastFetched: state.publicList.lastFetched,
        listLoading: state.publicList.loading,
        navbarSize: state.barSize.navbarSize,
    }
};

export default connect( mapStateToProps, { loadPublicList } ) (LeaderBoard);


function NoMatch({name}) {  
    return (
        <Alert severity='error' size='large'>
            <AlertTitle>watchlist not found!</AlertTitle>
            Public watchlist <span style={{color: 'blue', fontWeight: 'bold'}}>{name}</span> does not exists. 
            Select a watchlist from the menu.
        </Alert>
    );
}

function useQuery() {
    const { search } = useLocation();
  
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const LeftPanel = ({
    publicList,
    activeButton,
    cardColor,
    handleListItemClick,
    panelHeight,
    handleKeyPress
}) => {
    // Use total_return from the list instead of summing item returns
    const getTotalReturn = (list) => {
        return list.total_return || 0;
    };

    let { path, url } = useRouteMatch();

    return (
        <div style={{height: panelHeight, outline: 'none'}} tabIndex='-1'>
            <List dense={true}>
                {publicList.map( (v, idx) => {
                    const dateOnly = (v.created || '').split('T')[0];
                    return (
                        <Fragment key={idx}>
                        <Link to={`${path}/listname=${v.listname}`} style={{textDecoration: 'none'}}>
                        <ListItemButton key={idx}
                        onClick={(e) => handleListItemClick(v.listname, idx)}
                        sx={{backgroundColor: idx === activeButton ? '#bbd9fb' : cardColor}}
                        >
                            <div style={{paddingLeft: '10px'}} key={`div-${idx}`}>
                                <h6 key={`h1-${idx}`}>Name: { v.listname.toUpperCase() }</h6>
                                <h6 key={`h2-${idx}`}>Date: { dateOnly }</h6>
                                <h6 key={`h3-${idx}`}>Return: $ { getTotalReturn(v).toFixed(2) }</h6>
                            </div>
                        </ListItemButton>
                        </Link>
                        <hr />
                        </Fragment>
                    )
                })}
            </List>
        </div>
    )
}


const RightPanel = ({
    tweetMessage,
    csvData,
    activelistName,
    handleChartCopy,
    handleRefresh,

}) => {
    return (
        <Stack>
            <IconButton
            className="tweet"
            href={`https://twitter.com/intent/tweet?text=${tweetMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            >
            <Tooltip title="Share on Twitter">
                <TwitterIcon />
            </Tooltip>
            </IconButton>

            <IconButton>
            <FacebookShareButton url='https://watchlist101.com' quote={tweetMessage}>
            <Tooltip title="Share on Facebook">
                <FacebookIcon />
            </Tooltip>
            </FacebookShareButton>
            </IconButton>

            <IconButton>
            <CSVLink
            data={csvData}
            filename={activelistName + '.csv'}
            >
            <Tooltip title="Download the Watchlist">
                <FileDownloadOutlinedIcon sx={{color: '#495057'}} />
            </Tooltip>
            </CSVLink>
            </IconButton>

            <IconButton>
            <Tooltip title="Refresh List">
                <RefreshIcon onClick={() => handleRefresh()} />
            </Tooltip>
            </IconButton>

            <IconButton onClick={handleChartCopy}>
            <Tooltip title="Copy Chart">
            <ContentCopyOutlinedIcon/>
            </Tooltip>
            </IconButton>

        </Stack>
    )

}


const MainPanel = ({
    openlistName,
    openlistSymbols,
    openlistDates,
    rows,
    columns,
    cardColor,
    mainBoxHeight,
    chartRef,
}) => {


    return (
        <div style={{
            height: mainBoxHeight,
            backgroundColor: cardColor,
            marginBottom: '1rem',
        }}>
            <div style={{height: '50vh', marginBottom: '1rem'}}>
                <DataGrid
                rows={rows}
                columns={columns}
                hideFooter={true}
                />
            </div>
            <div>
            <div style={{background: 'honeydew'}}>
                <LineChart
                listName={openlistName}
                tkrList={openlistSymbols}
                startDateList={openlistDates}
                chartRef={ chartRef }
                width= "100%"
                height= "35vh"
                ></LineChart>
            </div>
            </div>
        </div>
    )
};
